'use client'

import { useState } from 'react'
import { useIntl } from 'react-intl'
import {
  Button,
  Cell,
  Divider,
  Dialog,
  Navigation,
  Spacer,
  Text,
  Icon,
  InfoBanner,
} from '@vinted/web-ui'

import { InfoCircle24, X24 } from '@vinted/monochrome-icons'

import { ItemPricingDetailsIcon } from '@marketplace-web/escrow-pricing/escrow-pricing-feature'
import { ItemBuyerOfferModal } from '@marketplace-web/offers/price-offers-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import {
  BundleSummaryModel,
  InstantBundleSummaryModel,
  BundleShippingType,
  DEFAULT_THUMBNAILS_LIMIT,
  PHONE_THUMBNAILS_LIMIT,
  BundleTransactionModel,
  clickEvent,
} from '@marketplace-web/bundles/items-bundle-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

import ItemThumbnails from '../ItemThumbnails'
import CurrencyConversionModal from '../CurrencyConversionModal'

type TransactionConfirmationProps = {
  bundleShippingType?: BundleShippingType | null
  redirectToCheckout?: boolean
  openOfferRequestModal?: boolean
}

type Props = {
  show: boolean
  onClose: () => void
  onConfirm: ({
    bundleShippingType,
    redirectToCheckout,
    openOfferRequestModal,
  }: TransactionConfirmationProps) => void
  selectedItems: Array<ProductItemModel> | null
  instantBundleSummary: InstantBundleSummaryModel
  bundleSummary: BundleSummaryModel
  bundleTrackingId: string | null
  sellerId: number
  bundleTransaction?: BundleTransactionModel | null
  onSkipNavigationWarning: () => void
}

const InstantBundleSummaryDialog = ({
  show,
  onClose,
  bundleSummary,
  instantBundleSummary,
  selectedItems,
  onConfirm,
  bundleTrackingId,
  sellerId,
  bundleTransaction,
  onSkipNavigationWarning,
}: Props) => {
  const translate = useTranslate('bundle_items.summary_modal')
  const { track } = useTracking()
  const { locale } = useIntl()

  const [isConfirmButtonLoading, setIsConfirmButtonLoading] = useState(false)
  const [isBuyButtonLoading, setIsBuyButtonLoading] = useState(false)
  const [isAskSellerButtonLoading, setIsAskSellerButtonLoading] = useState(false)
  const [isOfferRequestButtonLoading, setIsOfferRequestButtonLoading] = useState(false)
  const [areButtonsDisabled, setAreButtonsDisabled] = useState(false)

  const [isCurrencyConversionModalOpen, setIsCurrencyConversionModalOpen] = useState(false)
  const [isOfferRequestModalOpen, setIsOfferRequestModalOpen] = useState(false)

  const {
    itemsPrice,
    buyerProtectionFee,
    priceWithBuyerProtectionFee,
    conversion,
    shippingPrice,
    bundleShippingType,
    discountApplied,
    discountAmount,
    discountFraction,
  } = instantBundleSummary

  if (!selectedItems?.length) return null

  const handleConfirmButton = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (isConfirmButtonLoading) {
      event.preventDefault()

      return
    }

    setIsConfirmButtonLoading(true)
    setAreButtonsDisabled(true)

    onSkipNavigationWarning()
    onConfirm({ bundleShippingType })
  }

  const handleBuyButton = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (isBuyButtonLoading) {
      event.preventDefault()

      return
    }

    setIsBuyButtonLoading(true)
    setAreButtonsDisabled(true)

    onSkipNavigationWarning()
    onConfirm({ bundleShippingType, redirectToCheckout: true })
  }

  const handleAskSellerButton = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (isAskSellerButtonLoading) {
      event.preventDefault()

      return
    }

    setIsAskSellerButtonLoading(true)
    setAreButtonsDisabled(true)

    track(
      clickEvent({
        screen: 'bundle_summary',
        target: 'ask_seller',
        targetDetails: JSON.stringify({ bundle_id: bundleTrackingId }),
      }),
    )

    onSkipNavigationWarning()
    onConfirm({ bundleShippingType })
  }

  const handleOfferRequestButton = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (isOfferRequestButtonLoading) {
      event.preventDefault()

      return
    }

    setIsOfferRequestButtonLoading(true)
    setAreButtonsDisabled(true)

    onConfirm({
      bundleShippingType,
      redirectToCheckout: false,
      openOfferRequestModal: true,
    })

    setIsOfferRequestModalOpen(true)
    setIsOfferRequestButtonLoading(false)
    setAreButtonsDisabled(false)
  }

  const handleToggleCurrencyConversionModal = () =>
    setIsCurrencyConversionModalOpen(prevState => !prevState)

  const renderDiscount = () => {
    if (!discountApplied || !discountAmount) return null

    const discountPercentageAmount = Number(discountFraction).toLocaleString(locale, {
      style: 'percent',
    })

    return (
      <Cell
        styling={Cell.Styling.Tight}
        title={
          <Text
            as="span"
            text={translate('discount', { percentage: discountPercentageAmount })}
            theme="success"
          />
        }
        suffix={
          <Text
            as="span"
            text={formatCurrencyAmount(discountAmount, locale, { formatAsNegative: true })}
            theme="success"
          />
        }
        testId="instant-bundles-summary-modal-discount-cell"
      />
    )
  }

  const renderTotal = () => {
    if (!conversion) {
      return (
        <Cell
          styling={Cell.Styling.Tight}
          title={<Text as="span" text={translate('total')} bold theme="amplified" />}
          suffix={
            <Text
              as="span"
              text={formatCurrencyAmount(priceWithBuyerProtectionFee, locale)}
              theme="amplified"
              bold
            />
          }
          testId="instant-bundles-summary-modal-total-cell"
        />
      )
    }

    return (
      <Cell
        styling={Cell.Styling.Tight}
        title={
          <div className="u-flexbox u-align-items-center">
            <Text as="span" text={translate('total_converted')} bold theme="amplified" />
            <Button
              testId="instant-bundles-summary-modal-conversion-info-circle"
              icon={
                <Icon
                  color={Icon.Color.GreyscaleLevel2}
                  name={InfoCircle24}
                  aria={{
                    'aria-label': translate('a11y.currency_conversion_info'),
                  }}
                />
              }
              styling={Button.Styling.Flat}
              inline
              size={Button.Size.Small}
              onClick={handleToggleCurrencyConversionModal}
            />
          </div>
        }
        suffix={
          <Text
            as="span"
            text={formatCurrencyAmount(priceWithBuyerProtectionFee, locale)}
            theme="amplified"
            bold
          />
        }
        testId="instant-bundles-summary-modal-total-with-currency-cell"
      />
    )
  }

  const renderCurrencyConversionModal = () => {
    if (!conversion) return null

    const itemPrice = instantBundleSummary.totalPrice
    const protectionFee = instantBundleSummary.buyerProtectionFee

    return (
      <CurrencyConversionModal
        conversion={conversion}
        price={itemPrice.amount}
        buyerProtectionFee={protectionFee.amount}
        show={isCurrencyConversionModalOpen}
        isIntegratedShipping={false}
        onClose={handleToggleCurrencyConversionModal}
      />
    )
  }

  const renderOfferRequestModal = () => {
    if (!bundleSummary) return null

    const itemId = selectedItems[0]!.id.toString()
    const itemTitle = selectedItems[0]!.title
    const itemThumbnailUrl = selectedItems[0]!.thumbnailUrl

    const { totalPrice, currency } = bundleSummary

    return (
      <ItemBuyerOfferModal
        isBundlingDialogOpen={show}
        isOpen={isOfferRequestModalOpen}
        itemInfo={{
          itemId,
          itemIds: selectedItems.map(item => item.id),
          itemTitle,
          itemThumbnailUrl,
          sellerId,
          itemPrice: totalPrice.amount,
        }}
        currency={currency}
        bundleTransaction={bundleTransaction}
        onClose={() => setIsOfferRequestModalOpen(false)}
        onSkipNavigationWarning={onSkipNavigationWarning}
      />
    )
  }

  const renderDialogButtons = () => {
    if (instantBundleSummary.bundleShippingType === BundleShippingType.CustomOrNoShipping)
      return (
        <Button
          isLoading={isConfirmButtonLoading}
          disabled={areButtonsDisabled}
          text={translate('actions.request')}
          styling={Button.Styling.Filled}
          onClick={handleConfirmButton}
          testId="instant-bundles-summary-modal-confirm-button"
        />
      )

    if (instantBundleSummary.bundleShippingType === BundleShippingType.ShippingUnavailable)
      return (
        <Button
          isLoading={isAskSellerButtonLoading}
          disabled={areButtonsDisabled}
          text={translate('actions.ask_seller')}
          onClick={handleAskSellerButton}
          testId="instant-bundles-summary-modal-ask-seller-button"
        />
      )

    if (instantBundleSummary.bundleShippingType === BundleShippingType.Integrated)
      return (
        <SeparatedList separator={<Spacer size={Spacer.Size.Medium} />}>
          <Button
            isLoading={isBuyButtonLoading}
            disabled={areButtonsDisabled}
            text={translate('actions.buy')}
            styling={Button.Styling.Filled}
            onClick={handleBuyButton}
            testId="instant-bundles-summary-modal-buy-button"
          />
          <div className="u-flexbox u-gap-medium">
            <Button
              isLoading={isAskSellerButtonLoading}
              disabled={areButtonsDisabled}
              text={translate('actions.ask_seller')}
              onClick={handleAskSellerButton}
              testId="instant-bundles-summary-modal-ask-seller-button"
            />
            <Button
              isLoading={isOfferRequestButtonLoading}
              disabled={areButtonsDisabled}
              text={translate('actions.offer_request')}
              onClick={handleOfferRequestButton}
              testId="instant-bundles-summary-modal-offer-request-button"
            />
          </div>
        </SeparatedList>
      )

    return null
  }

  const renderShippingPrice = () => {
    if (bundleShippingType === BundleShippingType.CustomOrNoShipping)
      return (
        <Cell
          styling={Cell.Styling.Tight}
          body={<Text as="span" text={translate('shipping')} />}
          suffix={<Text as="span" text={translate('chosen_by_seller')} />}
          testId="instant-bundles-summary-modal-shipping-cell"
        />
      )

    if (shippingPrice) {
      return (
        <Cell
          styling={Cell.Styling.Tight}
          body={<Text as="span" text={translate('shipping')} />}
          suffix={<Text as="span" text={formatCurrencyAmount(shippingPrice, locale)} />}
          testId="instant-bundles-summary-modal-shipping-cell"
        />
      )
    }

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={<Text as="span" text={translate('shipping')} />}
        suffix={<Text as="span" text={translate('no_shipping_options')} />}
        testId="instant-bundles-summary-modal-shipping-cell"
      />
    )
  }

  const showCustomNoShippingBanner = bundleShippingType === BundleShippingType.CustomOrNoShipping

  return (
    <>
      <Dialog show={show} testId="instant-bundles-summary-modal">
        <Navigation
          right={
            <Button
              icon={<Icon name={X24} />}
              onClick={onClose}
              testId="instant-bundles-summary-modal-navigation-close-button"
              styling={Button.Styling.Flat}
              inline
            />
          }
          body={translate('title')}
        />
        <Divider />
        {showCustomNoShippingBanner && (
          <InfoBanner
            body={translate('custom_no_shipping_banner')}
            testId="custom-no-shipping-banner"
          />
        )}
        <Cell
          title={translate(
            'items_count',
            { count: selectedItems.length },
            { count: selectedItems.length },
          )}
          suffix={
            <div className="u-flexbox u-align-items-center">
              <div className="u-phones-only">
                <ItemThumbnails
                  visibleThumbnailsLimit={PHONE_THUMBNAILS_LIMIT}
                  items={selectedItems}
                />
              </div>
              <div className="u-tablets-up-only">
                <ItemThumbnails
                  visibleThumbnailsLimit={DEFAULT_THUMBNAILS_LIMIT}
                  items={selectedItems}
                />
              </div>
            </div>
          }
        />
        <Divider />
        <Cell>
          <SeparatedList separator={<Spacer size={Spacer.Size.Small} />}>
            <Cell
              styling={Cell.Styling.Tight}
              body={<Text as="span" text={translate('order')} />}
              suffix={<Text as="span" text={formatCurrencyAmount(itemsPrice, locale)} />}
              testId="instant-bundles-summary-modal-order-cell"
            />
            {renderShippingPrice()}
            <Cell
              styling={Cell.Styling.Tight}
              body={
                <div className="u-flexbox">
                  <Text as="span" text={translate('service_fee')} />
                  <Spacer size={Spacer.Size.Small} orientation={Spacer.Orientation.Vertical} />
                  <ItemPricingDetailsIcon
                    bundleId={bundleTrackingId}
                    itemId={selectedItems[0]!.id}
                  />
                </div>
              }
              suffix={<Text as="span" text={formatCurrencyAmount(buyerProtectionFee, locale)} />}
              testId="instant-bundles-summary-modal-service-fee-cell"
            />
          </SeparatedList>
          <Spacer size={Spacer.Size.Large} />
          <SeparatedList separator={<Spacer size={Spacer.Size.Medium} />}>
            {renderDiscount()}
            {renderTotal()}
            <Divider />
            {renderDialogButtons()}
          </SeparatedList>
        </Cell>
      </Dialog>
      {renderCurrencyConversionModal()}
      {renderOfferRequestModal()}
    </>
  )
}

export default InstantBundleSummaryDialog
